import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { rio } from '@amzn/rio-design-tokens';
import styled from '@emotion/styled';
// Shared by POP and TYP
export const ImageBlock = styled(Row) `
  position: relative;

  img {
    // Retain aspect ratio
    height: 100%;
    width: auto;
    border-radius: 4px;
  }
`;
// Shared by POP and TYP
export const QuantityBadge = styled(Row) `
  position: absolute;
  bottom: 4px;
  right: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.rio.semantic.color.background.primary};
  border: 1px solid ${({ theme }) => theme.rio.semantic.color.border.primary};
`;
/**
 * Sticky container for CTAs that can be positioned at top or bottom of viewport
 * Used for:
 * - Preview Order Page: Top sticky CTA with "Place Your Order" (position='top', default)
 * - Product Details Page: Bottom sticky CTA with "Continue with Amazon" (position='bottom')
 */
export const StickyContainer = styled(Column) `
  position: sticky;
  ${({ position = 'top' }) => (position === 'top' ? 'top: 0px;' : 'bottom: 0px;')}
  /**
   * Meridian Modals have a z-index of 60
   * Meridian Selectors have a z-index of 62
   * Meridan Headers have a z-index of 30
   * So the sticky header needs to be between 30 to 60
   */
  z-index: 50;
  background-color: ${({ theme }) => theme.rio.semantic.color.background.primary};
  box-shadow: ${({ theme, showShadow, position = 'top' }) => {
    if (!showShadow)
        return 'none';
    // Use Rio semantic shadow tokens based on position
    return position === 'top'
        ? theme.rio.semantic.shadow.low // Downward shadow for top positioned
        : theme.rio.semantic.shadow.top; // Upward shadow for bottom positioned
}};
`;
// We cannot override Meridian links so creating a custom component
export const Link = styled.a `
  &,
  &:hover,
  &:active {
    color: ${({ theme }) => theme.rio.semantic.color.link.default};
    text-decoration: ${({ underline }) => (underline ? 'underline' : 'none')};
  }
`;
// Deal badge text styling - shared between ProductCard and PriceBlock
export const DealText = styled(Text) `
  padding: 4px 8px;
  border-radius: 4px;
  background: ${({ theme }) => theme.rio.semantic.color.retail.deals};
  color: ${({ theme }) => theme.rio.semantic.color.font.light};
  width: fit-content;
`;
// Price-related text components shared across pricing displays
// Use secondary font color (gray) for light variant, otherwise use retail deals color (red)
// Light variant is used for FR marketplace non-Omnibus prices
export const DiscountText = styled(Text) `
  font-weight: 400 !important;
  color: ${({ theme, isLightVariant }) => isLightVariant ? theme.rio.semantic.color.font.secondary : theme.rio.semantic.color.retail.deals};
`;
// Used by ReviewImages, ProductCarousel, and Twister
export const ScrollableRow = styled(Row) `
  overflow-x: auto;
  white-space: nowrap;

  // Hide scrollbar for Parity with Rio.
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
export const RioText = styled(Text) `
  color: ${({ theme, rioColor }) => {
    if (rioColor === 'success' || rioColor === 'error') {
        return theme.rio.semantic.color.feedback[rioColor];
    }
    else if (rioColor === 'secondary') {
        return rio.base.color.gray[theme.mode === 'dark' ? 1100 : 500];
    }
    else if (rioColor === 'link') {
        return theme.rio.semantic.color.link.default;
    }
}};
`;
// Only to be used with "type=b200" font sizing
export const Paragraph = styled(RioText) `
  line-height: 18px !important;
  // Enable newlines
  white-space: pre-wrap;
`;
// Figma's 15/20 and 13/18 type, which Meridian Text lacks; use without `type` so `&&` outranks Meridian's default
export const BodyText = styled(RioText) `
  && {
    font-size: ${({ theme }) => theme.rio.base.size[15]};
    line-height: ${({ theme }) => theme.rio.base.size[20]};
    ${({ theme, rioColor }) => (rioColor ? '' : `color: ${theme.rio.semantic.color.font.primary};`)}
  }
`;
export const SmallText = styled(RioText) `
  && {
    font-size: ${({ theme }) => theme.rio.base.size[13]};
    line-height: ${({ theme }) => theme.rio.semantic.size.lineHeight.small};
    ${({ theme, rioColor }) => (rioColor ? '' : `color: ${theme.rio.semantic.color.font.primary};`)}
  }
`;
export const TruncatedParagraph = styled(Paragraph) `
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${({ clamp }) => clamp};
  line-clamp: ${({ clamp }) => clamp};
  word-break: break-word;
  overflow: hidden;
  // Disable newlines when truncated by default, enable when specified
  white-space: ${({ displayNewlines }) => (displayNewlines ? 'pre-wrap' : 'normal')};
`;
// Custom section divider which matches divider from 1.0.
// Built as a simple div instead of extending Meridian Divider since styled overrides don't work.
export const SectionDivider = styled.div `
  height: ${({ size = 'small' }) => (size === 'large' ? '8px' : '1px')};
  width: 100%;
  background-color: ${({ theme, size = 'small', subtle }) => {
    if (size === 'large') {
        return theme.rio.base.color.gray[theme.mode === 'dark' ? 300 : 1400];
    }
    if (subtle) {
        return theme.rio.base.color.gray[theme.mode === 'dark' ? 400 : 1300];
    }
    return theme.rio.base.color.gray[theme.mode === 'dark' ? 700 : 1000];
}};
`;
// Used on PDP and POP
export const ErrorBar = styled.div `
  background-color: ${({ theme }) => theme.rio.semantic.color.feedback.error};
  height: 85%;
  width: 4px;
  border-bottom-left-radius: 2px;
  border-bottom-right-radius: 2px;
  border-top-right-radius: 2px;
  border-top-left-radius: 2px;
`;
export const LoadingBar = styled.div `
  height: 12px;
  background: ${({ theme }) => theme.mode === 'dark'
    ? 'linear-gradient(90deg, #404040 0%, #606060 50%, #404040 100%)'
    : 'linear-gradient(90deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%)'};
  background-size: 200% 100%;
  border-radius: 4px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;
